import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Colors, Spacing, BorderRadius } from '../constants/theme';

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const SkeletonBase = styled.div<{ width?: string; height?: string }>`
  background: linear-gradient(90deg, ${Colors.border} 0%, ${Colors.borderLight} 50%, ${Colors.border} 100%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${BorderRadius.sm}px;
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '16px'};
`;

const SkeletonCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  margin-bottom: ${Spacing.md}px;
`;

const SkeletonHeader = styled(SkeletonBase)`
  height: 20px;
  margin-bottom: ${Spacing.md}px;
`;

const SkeletonLine = styled(SkeletonBase)<{ width?: string }>`
  height: 14px;
  margin-bottom: ${Spacing.sm}px;
  width: ${props => props.width || '100%'};
`;

const SkeletonAvatar = styled(SkeletonBase)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl}px;
`;

// Dashboard Card Skeleton
export const DashboardCardSkeleton: React.FC = () => (
  <SkeletonCard role="status" aria-label="Loading dashboard card">
    <SkeletonHeader width="60%" />
    <SkeletonBase height="48px" width="40%" />
    <div style={{ marginTop: Spacing.lg }}>
      <SkeletonLine width="80%" />
      <SkeletonLine width="60%" />
    </div>
    <span className="sr-only">Loading dashboard data...</span>
  </SkeletonCard>
);

// Profile Header Skeleton
export const ProfileHeaderSkeleton: React.FC = () => (
  <SkeletonCard role="status" aria-label="Loading profile information">
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: Spacing.lg }}>
      <SkeletonAvatar />
      <div style={{ marginLeft: Spacing.md, flex: 1 }}>
        <SkeletonHeader width="200px" />
        <SkeletonLine width="150px" />
      </div>
    </div>
    <SkeletonLine width="100%" />
    <SkeletonLine width="85%" />
    <span className="sr-only">Loading profile information...</span>
  </SkeletonCard>
);

// Table Row Skeleton
export const TableRowSkeleton: React.FC<{ columns?: number }> = ({ columns = 4 }) => (
  <tr role="status" aria-label="Loading table row">
    {Array.from({ length: columns }, (_, i) => (
      <td key={i} style={{ padding: `${Spacing.sm}px` }}>
        <SkeletonLine height="16px" />
      </td>
    ))}
    <span className="sr-only">Loading table data...</span>
  </tr>
);

// Dashboard Grid Skeleton
export const DashboardGridSkeleton: React.FC<{ cards?: number }> = ({ cards = 6 }) => (
  <SkeletonGrid role="status" aria-label="Loading dashboard">
    {Array.from({ length: cards }, (_, i) => (
      <DashboardCardSkeleton key={i} />
    ))}
    <span className="sr-only">Loading dashboard cards...</span>
  </SkeletonGrid>
);

// Text Block Skeleton
export const TextBlockSkeleton: React.FC<{ lines?: number }> = ({ lines = 3 }) => (
  <div role="status" aria-label="Loading text content">
    {Array.from({ length: lines }, (_, i) => (
      <SkeletonLine 
        key={i} 
        width={i === lines - 1 ? '65%' : '100%'} 
      />
    ))}
    <span className="sr-only">Loading content...</span>
  </div>
);

export default SkeletonBase;